import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save } from 'lucide-react';
import DatePicker, { registerLocale } from 'react-datepicker';
import { vi } from 'date-fns/locale';
import 'react-datepicker/dist/react-datepicker.css';
import SystemHeader from '../SystemHeader';
import ImageUploadField from './image-upload-field';
import ImageModal from './image-modal';
import { uploadImage } from '../../services/feature-api';

registerLocale('vi', vi);

/* ═══════════════════════════════════════════════════════════════
   NUMBER INPUT HELPER
   ═══════════════════════════════════════════════════════════════ */
// Parses user-typed numbers: "20k" → 20000, "1.5k" → 1500, strips other non-digits
const parseNumberInput = (val) => {
  if (val === '' || val === null || val === undefined) return '';
  const str = String(val).trim();
  const kMatch = str.match(/^([\d.]+)[kK]$/);
  if (kMatch) {
    const num = parseFloat(kMatch[1]);
    return isNaN(num) ? '' : String(Math.round(num * 1000));
  }
  const stripped = str.replace(/[^0-9]/g, '');
  return stripped;
};

/* ═══════════════════════════════════════════════════════════════
   VIETNAMESE VALIDATION MESSAGES
   ═══════════════════════════════════════════════════════════════ */
const VI_MESSAGES = {
  valueMissing:  'Vui lòng điền trường này.',
  typeMismatch_url: 'Vui lòng nhập URL hợp lệ (bắt đầu bằng https://...).',
  typeMismatch_email: 'Vui lòng nhập email hợp lệ.',
  typeMismatch:  'Giá trị không hợp lệ.',
  rangeUnderflow: (min) => `Giá trị phải lớn hơn hoặc bằng ${min}.`,
  rangeOverflow:  (max) => `Giá trị phải nhỏ hơn hoặc bằng ${max}.`,
  badInput:      'Vui lòng nhập giá trị hợp lệ.',
};

const applyViValidation = (el) => {
  if (!el) return;
  const handler = () => {
    if (el.validity.valueMissing) {
      el.setCustomValidity(VI_MESSAGES.valueMissing);
    } else if (el.validity.typeMismatch) {
      const key = `typeMismatch_${el.type}`;
      el.setCustomValidity(VI_MESSAGES[key] || VI_MESSAGES.typeMismatch);
    } else if (el.validity.rangeUnderflow) {
      el.setCustomValidity(VI_MESSAGES.rangeUnderflow(el.min));
    } else if (el.validity.rangeOverflow) {
      el.setCustomValidity(VI_MESSAGES.rangeOverflow(el.max));
    } else if (el.validity.badInput) {
      el.setCustomValidity(VI_MESSAGES.badInput);
    } else {
      el.setCustomValidity('');
    }
  };
  el.addEventListener('invalid', handler);
  el.addEventListener('input', () => el.setCustomValidity(''));
};

/* ═══════════════════════════════════════════════════════════════ */

const buildInitialState = (fields, initialData) => {
  // Use Vietnam timezone (UTC+7) to get the correct 'today' date string
  const vnStr = new Date().toLocaleString("en-US", { timeZone: "Asia/Ho_Chi_Minh" });
  const vnDate = new Date(vnStr);
  const today = `${vnDate.getFullYear()}-${String(vnDate.getMonth() + 1).padStart(2, '0')}-${String(vnDate.getDate()).padStart(2, '0')}`;
  
  const state = {};
  fields.forEach((field) => {
    if (initialData && initialData[field.key] !== undefined) {
      state[field.key] = initialData[field.key];
    } else if (field.defaultValue !== undefined) {
      state[field.key] = field.defaultValue;
    } else if (field.type === 'date') {
      state[field.key] = today;
    } else {
      state[field.key] = '';
    }
  });
  return state;
};

const getReadonlyDisplay = (field, value) => {
  if (field.type === 'select' && field.options) {
    const opt = field.options.find((o) => o.value === value);
    return opt ? opt.label : value || '—';
  }
  return value || '—';
};

// ─── Field Renderer with Vietnamese validation ref ──────────
const FieldRenderer = ({ field, value, onChange, disabled, onViewImage }) => {
  const inputRef = useRef(null);

  useEffect(() => {
    applyViValidation(inputRef.current);
  }, []);

  const baseInput =
    'w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white ' +
    'focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent ' +
    'disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed';

  // Readonly/locked fields
  if (field.readonly || field._editReadonly) {
    return (
      <div className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50 text-gray-500">
        {getReadonlyDisplay(field, value)}
      </div>
    );
  }

  switch (field.type) {
    case 'auto-id':
      return (
        <div className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50 text-gray-400 font-mono">
          {value || 'Tự động tạo khi lưu'}
        </div>
      );

    case 'textarea':
      return (
        <textarea
          ref={inputRef}
          value={value}
          onChange={(e) => onChange(field.key, e.target.value)}
          disabled={disabled}
          rows={3}
          required={field.required}
          placeholder={field.placeholder || ''}
          className={baseInput + ' resize-none'}
        />
      );

    case 'select':
      return (
        <select
          ref={inputRef}
          value={value}
          onChange={(e) => onChange(field.key, e.target.value)}
          disabled={disabled}
          required={field.required}
          className={baseInput}
        >
          <option value="">Chọn {field.label.toLowerCase()}</option>
          {(field.options || []).map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      );

    case 'searchable-select': {
      // Searchable dropdown with filter input
      const SearchableSelect = () => {
        const [open, setOpen] = useState(false);
        const [search, setSearch] = useState('');
        const wrapRef = useRef(null);
        const options = field.options || [];
        const selectedLabel = options.find(o => o.value === value)?.label || '';
        const filtered = search
          ? options.filter(o => o.label.toLowerCase().includes(search.toLowerCase()))
          : options;

        useEffect(() => {
          const handler = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
          document.addEventListener('mousedown', handler);
          return () => document.removeEventListener('mousedown', handler);
        }, []);

        return (
          <div ref={wrapRef} className="relative">
            <button type="button" onClick={() => setOpen(!open)} disabled={disabled}
              className={baseInput + ' text-left flex items-center justify-between'}>
              <span className={value ? 'text-gray-900' : 'text-gray-400'}>
                {selectedLabel || `Chọn ${field.label.toLowerCase()}`}
              </span>
              <span className="material-symbols-rounded text-gray-400" style={{fontSize:'18px'}}>expand_more</span>
            </button>
            {open && (
              <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-xl shadow-lg max-h-60 overflow-hidden">
                <div className="p-2 border-b border-gray-100">
                  <input type="text" autoFocus placeholder="Tìm kiếm..." value={search}
                    onChange={e => setSearch(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-300" />
                </div>
                <div className="overflow-y-auto max-h-44">
                  {filtered.length === 0 ? (
                    <p className="text-sm text-gray-400 text-center py-3">Không tìm thấy</p>
                  ) : filtered.map(opt => (
                    <button key={opt.value} type="button"
                      onClick={() => { onChange(field.key, opt.value); setOpen(false); setSearch(''); }}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 transition-colors ${opt.value === value ? 'bg-blue-50 font-semibold text-blue-700' : 'text-gray-700'}`}>
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      };
      return <SearchableSelect />;
    }

    case 'image':
      return (
        <ImageUploadField
          label={null}
          value={value}
          onChange={(file) => onChange(field.key, file)}
          required={field.required}
          disabled={disabled}
          onViewImage={onViewImage}
        />
      );

    case 'number':
      return (
        <input
          ref={inputRef}
          type="text"
          inputMode="numeric"
          value={value}
          onChange={(e) => onChange(field.key, parseNumberInput(e.target.value))}
          disabled={disabled}
          required={field.required}
          placeholder={field.placeholder || '0'}
          pattern="[0-9]*"
          className={baseInput}
        />
      );

    case 'url':
      return (
        <input
          ref={inputRef}
          type="url"
          value={value}
          onChange={(e) => onChange(field.key, e.target.value)}
          disabled={disabled}
          required={field.required}
          placeholder={field.placeholder || 'https://...'}
          className={baseInput}
        />
      );

    case 'date': {
      // Date restriction: if maxDaysBack is set, limit selectable dates
      const vnNowStr = new Date().toLocaleString("en-US", { timeZone: "Asia/Ho_Chi_Minh" });
      const vnNow = new Date(vnNowStr);
      const todayDate = new Date(vnNow.getFullYear(), vnNow.getMonth(), vnNow.getDate());
      let minDate = null;
      let maxDate = null;
      if (field.maxDaysBack != null) {
        minDate = new Date(todayDate);
        minDate.setDate(minDate.getDate() - field.maxDaysBack);
        maxDate = todayDate;
      }
      return (
        <DatePicker
          selected={value ? new Date(value + 'T00:00:00') : null}
          onChange={(date) => {
            if (date) {
              const y = date.getFullYear();
              const m = String(date.getMonth() + 1).padStart(2, '0');
              const d = String(date.getDate()).padStart(2, '0');
              onChange(field.key, `${y}-${m}-${d}`);
            } else {
              onChange(field.key, '');
            }
          }}
          dateFormat="dd/MM/yyyy"
          locale="vi"
          disabled={disabled}
          required={field.required}
          className={baseInput}
          placeholderText="Chọn ngày..."
          isClearable
          showPopperArrow={false}
          minDate={minDate}
          maxDate={maxDate}
        />
      );
    }

    default:
      return (
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => onChange(field.key, e.target.value)}
          disabled={disabled}
          required={field.required}
          placeholder={field.placeholder || ''}
          className={baseInput}
        />
      );
  }
};

/**
 * GenericFeatureForm — Config-driven form with:
 *  • Step numbers (colored badges)
 *  • Hint text below labels
 *  • Vietnamese validation messages
 *  • Separate create/edit visibility & readonly
 *  • Improved back button
 */
const GenericFeatureForm = ({
  title,
  fields = [],
  onSubmit,
  initialData = null,
  backPath,
  accentColor,
  userInfo,
  accessToken,
  onLogout,
  totalPoints,
  onRefreshPoints,
  lockOnApproval = false,
}) => {
  const navigate = useNavigate();
  const isEdit = Boolean(initialData);
  const accent = accentColor || '#1E5A35';

  // Apply create/edit rules: filter visible fields, mark editReadonly
  const processedFields = fields
    .filter((f) => {
      if (f.visible === false) return false;
      if (isEdit && f.editVisible === false) return false;
      return true;
    })
    .map((f) => ({
      ...f,
      _editReadonly: isEdit && f.editReadonly === true,
    }));

  // Sort by editOrder if available (for edit mode)
  if (isEdit) {
    processedFields.sort((a, b) => {
      const oa = a.editOrder != null ? a.editOrder : 999;
      const ob = b.editOrder != null ? b.editOrder : 999;
      if (oa !== 999 || ob !== 999) return oa - ob;
      return 0; // preserve original order
    });
  }

  // Lock editing if record is approved/rejected and lockOnApproval is enabled
  const isLockedByApproval = isEdit && lockOnApproval &&
    (initialData?.companyApproved === 'approved' || initialData?.companyApproved === 'rejected');

  const [formData, setFormData] = useState(() => buildInitialState(fields, initialData));
  const [modalImageUrl, setModalImageUrl] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    setFormData(buildInitialState(fields, initialData));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData]);

  const handleChange = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
    setSuccessMsg('');
    setErrorMsg('');
  };

  const processImageUploads = async (data) => {
    const processed = { ...data };
    for (const field of fields) {
      if (field.type === 'image' && processed[field.key] instanceof File) {
        const result = await uploadImage(accessToken, processed[field.key]);
        if (!result.success) throw new Error(`Lỗi tải ảnh "${field.label}": ${result.error}`);
        processed[field.key] = result.imageUrl;
      }
    }
    return processed;
  };

  const stripAutoFields = (data) => {
    const cleaned = { ...data };
    fields.forEach((field) => {
      if (field.type === 'auto-id') delete cleaned[field.key];
    });
    return cleaned;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSuccessMsg('');
    setErrorMsg('');

    // Custom validation for fields that bypass native HTML validation
    // (image, searchable-select use custom UI, not native <input required>)
    for (const field of processedFields) {
      if (!field.required || field.readonly || field._editReadonly) continue;
      const val = formData[field.key];
      if (field.type === 'image') {
        if (!val) {
          setErrorMsg(`Vui lòng chọn ảnh cho "${field.label}".`);
          setSubmitting(false);
          return;
        }
      }
      if (field.type === 'searchable-select') {
        if (!val) {
          setErrorMsg(`Vui lòng chọn "${field.label}".`);
          setSubmitting(false);
          return;
        }
      }
    }

    try {
      const processedData = stripAutoFields(await processImageUploads(formData));
      await onSubmit(processedData, accessToken);
      setSuccessMsg(isEdit ? 'Cập nhật thành công!' : 'Tạo mới thành công!');
      setTimeout(() => navigate(backPath || '/dashboard'), 1200);
    } catch (err) {
      setErrorMsg('Có lỗi xảy ra: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <SystemHeader
        userInfo={userInfo}
        onLogout={onLogout}
        totalPoints={totalPoints}
        pageTitle={title}
        onRefreshPoints={onRefreshPoints}
      />

      <div className="flex-1 max-w-2xl w-full mx-auto flex flex-col">
        {/* Top bar with improved back button */}
        <div className="flex items-center gap-3 px-4 py-3 bg-white border-b border-gray-200">
          <button
            type="button"
            onClick={() => navigate(backPath || '/dashboard')}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors"
            style={{ color: accent, background: accent + '12' }}
          >
            <ArrowLeft size={16} />
            <span>Quay lại</span>
          </button>
          <h1 className="text-base font-semibold flex-1" style={{ color: accent }}>
            {title}
          </h1>
        </div>

        {/* Lock on approval banner */}
        {isLockedByApproval && (
          <div className={`mx-4 mt-3 p-3 text-sm rounded-lg flex items-center gap-2 ${
            initialData?.companyApproved === 'rejected'
              ? 'bg-red-50 border border-red-300 text-red-800'
              : 'bg-amber-50 border border-amber-300 text-amber-800'
          }`}>
            <span className="material-symbols-rounded" style={{fontSize:'18px'}}>lock</span>
            <span>{initialData?.companyApproved === 'rejected'
              ? 'Bản ghi đã bị từ chối, không thể chỉnh sửa.'
              : 'Bản ghi đã được duyệt, không thể chỉnh sửa.'}</span>
          </div>
        )}

        {/* Approval notes */}
        {isLockedByApproval && initialData?.notes && (
          <div className="mx-4 mt-3">
            <label className="text-sm font-medium text-gray-700 mb-1 block">Ghi chú duyệt</label>
            <div className="p-3 bg-gray-50 border border-gray-200 text-sm text-gray-700 rounded-lg whitespace-pre-wrap">
              {initialData.notes}
            </div>
          </div>
        )}

        {/* Feedback messages */}
        {successMsg && (
          <div className="mx-4 mt-3 p-3 bg-green-50 border border-green-200 text-green-700 text-sm rounded-lg">
            {successMsg}
          </div>
        )}
        {/* Form with step numbers */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-4 py-4 pb-6 space-y-4">
          {processedFields.map((field, index) => (
            <div key={field.key} className="relative">
              {/* Step number + label row */}
              <div className="flex items-center gap-2 mb-1.5">
                <span
                  className="flex-shrink-0 w-6 h-6 rounded-lg text-white text-xs font-bold flex items-center justify-content"
                  style={{ backgroundColor: accent, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                >
                  {index + 1}
                </span>
                <label className="text-sm font-medium text-gray-700">
                  {field.label}
                  {field.required && field.type !== 'auto-id' && !field.readonly && !field._editReadonly && (
                    <span className="text-red-500 ml-1">*</span>
                  )}
                  {field._editReadonly && (
                    <span className="text-[10px] ml-2 text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">🔒 Chỉ xem</span>
                  )}
                </label>
              </div>
              {/* Hint text */}
              {field.hint && (
                <p className="text-[11px] text-gray-400 mb-1.5 ml-8 italic">{field.hint}</p>
              )}
              {/* Field input indented */}
              <div className="ml-8">
                <FieldRenderer
                  field={field}
                  value={formData[field.key]}
                  onChange={handleChange}
                  disabled={submitting || isLockedByApproval || field.type === 'auto-id' || field._editReadonly}
                  onViewImage={(url) => setModalImageUrl(url)}
                />
              </div>
            </div>
          ))}

          {/* Validation/error message near submit button */}
          {errorMsg && (
            <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg">
              {errorMsg}
            </div>
          )}

          {/* Submit button */}
          <div className="pt-2">
            <button
              type="submit"
              disabled={submitting || isLockedByApproval}
              className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-lg text-white font-medium text-sm transition-all disabled:opacity-60 disabled:cursor-not-allowed"
              style={{ backgroundColor: submitting ? '#6b7280' : accent }}
            >
              {submitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Đang lưu...</span>
                </>
              ) : (
                <>
                  <Save size={16} />
                  <span>{isEdit ? 'Cập nhật' : 'Tạo mới'}</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      <ImageModal imageUrl={modalImageUrl} onClose={() => setModalImageUrl(null)} />
    </div>
  );
};

export default GenericFeatureForm;
